import { useFormContext } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  BUCKET_DESCRIPTIONS,
  BUCKET_LABELS,
  MULTIPLIER_MAX,
  MULTIPLIER_MIN,
  OUTCOME_MODES,
} from './constants';

/**
 * One bucket's outcome. Exclude is set apart from the two ordering options
 * rather than sitting in the same group — it is the only one here that makes
 * content invisible instead of just reordering it.
 */
export default function OutcomeField({ bucket }) {
  const { control, setValue, watch } = useFormContext();
  const mode = watch(`${bucket}.mode`);

  const setMode = next => {
    setValue(`${bucket}.mode`, next, { shouldDirty: true, shouldValidate: true });
    if (next !== OUTCOME_MODES.MULTIPLIER) {
      setValue(`${bucket}.multiplier`, '', { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <FormField
      control={control}
      name={`${bucket}.multiplier`}
      render={({ field }) => (
        <FormItem className="space-y-2 rounded-md border p-3">
          <div>
            <FormLabel className="font-bold">{BUCKET_LABELS[bucket]}</FormLabel>
            <p className="text-muted-foreground text-xs">{BUCKET_DESCRIPTIONS[bucket]}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === OUTCOME_MODES.NO_CHANGE ? 'default' : 'outline'}
              onClick={() => setMode(OUTCOME_MODES.NO_CHANGE)}>
              No change
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === OUTCOME_MODES.MULTIPLIER ? 'default' : 'outline'}
              onClick={() => setMode(OUTCOME_MODES.MULTIPLIER)}>
              Multiplier
            </Button>

            {mode === OUTCOME_MODES.MULTIPLIER && (
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground text-sm">×</span>
                <Input
                  {...field}
                  type="number"
                  step="0.1"
                  min={MULTIPLIER_MIN}
                  max={MULTIPLIER_MAX}
                  placeholder="10"
                  className="h-9 w-20"
                />
                <span className="text-muted-foreground text-xs">
                  {MULTIPLIER_MIN}–{MULTIPLIER_MAX}
                </span>
              </div>
            )}

            <Button
              type="button"
              size="sm"
              variant={mode === OUTCOME_MODES.EXCLUDE ? 'destructive' : 'outline'}
              className="ml-auto"
              onClick={() => setMode(OUTCOME_MODES.EXCLUDE)}>
              Exclude
            </Button>
          </div>

          {mode === OUTCOME_MODES.EXCLUDE && (
            <p className="text-destructive text-xs">
              Removed from the results and from the result count.
            </p>
          )}

          <FormMessage />
        </FormItem>
      )}
    />
  );
}
